// A ranking (P3): the field's options as a list the respondent puts in order.
// Up/down buttons always work; dragging a row is the enhancement. The answer
// is the ordered list of option values, written whole on the first move (or
// on "Keep this order"), so an untouched list stays blank and a required one
// can be told from one the respondent agreed with. An option added to the
// form after a draft was saved lands at the end, unranked, until the next move.
import { useState } from 'react';
import type { FieldDefinition } from '../shared/types';
import { useMediaQuery } from '../shared/media';
import { STRINGS as S } from './strings';

/** No pointer to drag with: the hint must not offer a path that does not exist. */
const NO_HOVER = '(hover: none)';

export default function RankingInput({ def, domId, value, onChange, disabled }: {
  def: FieldDefinition;
  domId: string;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const opts = def.options || [];
  const known = new Set(opts.map((o) => o.value));
  const ranked = Array.isArray(value) ? (value as unknown[]).map(String).filter((v) => known.has(v)) : [];
  const order = [...ranked, ...opts.map((o) => o.value).filter((v) => !ranked.includes(v))];
  const label = (v: string) => opts.find((o) => o.value === v)?.text || v;
  const answered = ranked.length > 0;
  const [dragging, setDragging] = useState<number | null>(null);
  const noDrag = useMediaQuery(NO_HOVER);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  if (!opts.length) return null;
  return (
    <div className="of-rank">
      <ol className="of-rank-list" aria-describedby={`${domId}-rank-hint`}>
        {order.map((v, i) => (
          <li
            key={v}
            className={`of-rank-item${answered ? '' : ' unranked'}${dragging === i ? ' dragging' : ''}`}
            draggable={!disabled}
            onDragStart={(e) => { setDragging(i); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { if (dragging !== null) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); if (dragging !== null) move(dragging, i); setDragging(null); }}
            onDragEnd={() => setDragging(null)}
          >
            <span className="of-rank-n" aria-hidden="true">{i + 1}</span>
            <span className="of-rank-label">{label(v)}</span>
            <span className="of-rank-btns">
              <button type="button" className="of-rank-btn" disabled={disabled || i === 0}
                aria-label={S.rank.up(label(v))} onClick={() => move(i, i - 1)}>▲</button>
              <button type="button" className="of-rank-btn" disabled={disabled || i === order.length - 1}
                aria-label={S.rank.down(label(v))} onClick={() => move(i, i + 1)}>▼</button>
            </span>
          </li>
        ))}
      </ol>
      <div className="of-rank-foot">
        <span className="of-info" id={`${domId}-rank-hint`}>{noDrag ? S.rank.hintTap : S.rank.hint}</span>
        {!answered && !disabled && (
          <button type="button" className="of-rank-keep" onClick={() => onChange(order)}>{S.rank.keep}</button>
        )}
      </div>
    </div>
  );
}
